import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useMailFactory from '../../hooks/useMailFactory';
import { useAuth } from '../../context/authContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const isDevEnvironment = Boolean(import.meta.env?.DEV) || import.meta.env?.VITE_IS_DEV === 'true';

const TARGET_OPTIONS = [
  { value: 'internalBooking', label: 'Internal Booking' },
  { value: 'temporaryReservation', label: 'Temporäre Reservierung' },
];

const formatBookingLabel = (item, targetType) => {
  if (!item) return '';
  if (targetType === 'internalBooking') {
    const date = item.startTime ? dayjs(item.startTime).format('DD.MM.YYYY HH:mm') : '—';
    return `${item.bookingNumber || '—'} · ${item.firstName || ''} ${item.lastName || ''} · ${date}`;
  }

  const date = item.startTime ? dayjs(item.startTime).format('DD.MM.YYYY HH:mm') : '—';
  return `${item.mail || '—'} · ${date}`;
};

export default function MailFactoryView() {
  const authCtx = useAuth();
  const { getMailTypes, searchTargets, sendMail } = useMailFactory();
  const searchInputRef = useRef(null);
  const getMailTypesRef = useRef(getMailTypes);
  const searchTargetsRef = useRef(searchTargets);
  const sendMailRef = useRef(sendMail);

  useEffect(() => {
    getMailTypesRef.current = getMailTypes;
  }, [getMailTypes]);

  useEffect(() => {
    searchTargetsRef.current = searchTargets;
  }, [searchTargets]);

  useEffect(() => {
    sendMailRef.current = sendMail;
  }, [sendMail]);

  const [targetType, setTargetType] = useState('internalBooking');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [mailTypes, setMailTypes] = useState([]);
  const [selectedMailKey, setSelectedMailKey] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isFetchingTypes, setIsFetchingTypes] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const selectedTargetId = selectedTarget?._id;

  const loadMailTypes = useCallback(
    async targetId => {
      try {
        setIsFetchingTypes(true);
        const types = await getMailTypesRef.current({
          targetType,
          targetId,
        });
        setMailTypes(types);
      } catch (error) {
        toast.error('Fehler beim Laden der Mail-Typen');
      } finally {
        setIsFetchingTypes(false);
      }
    },
    [targetType]
  );

  useEffect(() => {
    loadMailTypes();
    setSelectedMailKey('');
  }, [loadMailTypes]);

  useEffect(() => {
    if (selectedTargetId) {
      loadMailTypes(selectedTargetId);
    }
  }, [selectedTargetId, loadMailTypes]);

  useEffect(() => {
    if (mailTypes.length === 0) {
      setSelectedMailKey('');
      return;
    }

    setSelectedMailKey(prev => {
      if (prev && mailTypes.some(type => type.key === prev)) {
        return prev;
      }
      return mailTypes[0].key;
    });
  }, [mailTypes]);

  const handleTargetTypeChange = useCallback((_, newValue) => {
    if (!newValue) return;
    setTargetType(newValue);
    setSelectedTarget(null);
    setSearchResults([]);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  }, []);

  const handleSearch = useCallback(
    async event => {
      event.preventDefault();
      const query = searchInputRef.current?.value?.trim();
      if (!query || query.length < 2) {
        toast.info('Bitte mindestens zwei Zeichen eingeben.');
        return;
      }

      setIsSearching(true);
      try {
        const results = await searchTargetsRef.current({
          targetType,
          searchTerm: query,
        });
        setSearchResults(results);
      } catch (error) {
        toast.error('Fehler bei der Suche.');
      } finally {
        setIsSearching(false);
      }
    },
    [targetType]
  );

  const handleSelectTarget = useCallback(result => {
    setSelectedTarget(result);
  }, []);

  const loadLatestResults = useCallback(async currentTargetType => {
    setIsSearching(true);
    try {
      const latestResults = await searchTargetsRef.current({
        targetType: currentTargetType,
      });
      setSearchResults(latestResults);
    } catch (error) {
      toast.error('Fehler beim Laden der neuesten Datensätze.');
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const query = searchInputRef.current?.value?.trim();
    if (query) {
      return;
    }
    loadLatestResults(targetType);
  }, [targetType, loadLatestResults]);

  const handleSendMail = useCallback(async () => {
    if (!selectedTarget) {
      toast.info('Bitte zuerst einen Datensatz auswählen.');
      return;
    }
    if (!selectedMailKey) {
      toast.info('Bitte einen Mail-Typ auswählen.');
      return;
    }

    setIsSending(true);
    try {
      await sendMailRef.current({
        targetType,
        targetId: selectedTarget._id,
        mailKey: selectedMailKey,
      });
      toast.success('Mail wurde ausgelöst.');
    } catch (error) {
      toast.error('Fehler beim Auslösen der Mail.');
    } finally {
      setIsSending(false);
    }
  }, [targetType, selectedTarget, selectedMailKey]);

  const selectedTargetLabel = useMemo(
    () => formatBookingLabel(selectedTarget, targetType),
    [selectedTarget, targetType]
  );

  const renderResultDetails = useCallback(
    item => {
      if (targetType === 'internalBooking') {
        const date = item.startTime ? dayjs(item.startTime).format('DD.MM.YYYY HH:mm') : '—';
        return `${item.firstName || ''} ${item.lastName || ''} · ${date} · ${item.mail || '—'}`;
      }

      const date = item.startTime ? dayjs(item.startTime).format('DD.MM.YYYY HH:mm') : '—';
      return `${date} · ${item.mail || '—'}`;
    },
    [targetType]
  );

  if (!isDevEnvironment) {
    return <Navigate to='/welcome' replace />;
  }

  if (!authCtx.checkRight('dev:tool:mailfactory')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ padding: 3, ...viewRootTextPrimarySx }}>
      <Paper sx={{ padding: 3, marginBottom: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Zieltyp auswählen
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={targetType}
              onChange={handleTargetTypeChange}
              size='small'
            >
              {TARGET_OPTIONS.map(option => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box component='form' onSubmit={handleSearch}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                inputRef={searchInputRef}
                placeholder='Mail, Buchungsnummer oder Kunde'
              />
              <Button type='submit' variant='contained' disabled={isSearching}>
                {isSearching ? <CircularProgress size={20} /> : 'Suchen'}
              </Button>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Ergebnisse
            </Typography>
            {isSearching && (
              <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!isSearching && searchResults.length === 0 && (
              <Typography variant='body2' color='text.secondary'>
                Noch keine Ergebnisse gefunden.
              </Typography>
            )}
            <List dense>
              {searchResults.map(result => (
                <ListItem
                  key={result._id}
                  secondaryAction={
                    <Button
                      size='small'
                      variant={selectedTarget?._id === result._id ? 'contained' : 'outlined'}
                      onClick={() => handleSelectTarget(result)}
                    >
                      {selectedTarget?._id === result._id ? 'Ausgewählt' : 'Auswählen'}
                    </Button>
                  }
                >
                  <ListItemText
                    primary={formatBookingLabel(result, targetType)}
                    secondary={renderResultDetails(result)}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ padding: 3 }}>
        <Stack spacing={2}>
          <Typography variant='h6'>Mail versenden</Typography>

          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Ausgewählter Datensatz
            </Typography>
            {selectedTarget ? (
              <Chip label={selectedTargetLabel} color='primary' />
            ) : (
              <Typography variant='body2' color='text.secondary'>
                Noch kein Datensatz ausgewählt.
              </Typography>
            )}
          </Box>

          <FormControl fullWidth disabled={mailTypes.length === 0 || isFetchingTypes}>
            <InputLabel id='mail-type-select-label'>Mail-Typ</InputLabel>
            <Select
              labelId='mail-type-select-label'
              label='Mail-Typ'
              value={selectedMailKey}
              onChange={event => setSelectedMailKey(event.target.value)}
            >
              {mailTypes.map(type => (
                <MenuItem key={type.key} value={type.key}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Button
              variant='contained'
              disabled={!selectedTarget || !selectedMailKey || isSending}
              onClick={handleSendMail}
            >
              {isSending ? <CircularProgress size={20} /> : 'Mail auslösen'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
